<template>
  <div>
    <table class="directory-table">
      <thead>
        <tr>
          <th
            v-for="(header, index) in headers"
            :key="index"
            :class="header.cls"
            :colspan="index === 0 ? '2' : '1'"
          >
            {{ header.title }}
          </th>
          <th
            v-if="showAction"
            class="action"
          />
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in tableContent">
          <template v-for="(company, cIndex) in item.companies">
            <tr
              v-show="cIndex === 0 || !collapseItems[index]"
              :key="company.id"
              :dirId="item.id"
              :class="['main-comp', (index + 1) % 2 === 1 ? 'odd-item' : 'even-item']"
            >
              <td class="collapse-icon">
                <a
                  v-if="cIndex === 0 && company.subCompanies.length !== 0"
                  class="arrow-down"
                  :class="{ 'arrow-up': collapseItems[index] }"
                  @click="collapse(index)"
                />
              </td>
              <td
                v-for="(header, j) in headers"
                :key="`${header.attr}_${j}`"
                :class="header.cls"
              >
                <div
                  class="directory-table__value"
                  :class="{'field-hightLight': setHightLigth(item, `companies.${cIndex}.${header.attr}`)}"
                >
                  <router-link
                    v-if="header.routerLink"
                    :to="{ name: detailRouterName, params: { id: item.id }}"
                    v-html="vHtmlMainValue({header, cIndex, company})"
                  />
                  <span
                    v-else
                    v-html="vHtmlMainValue({header, cIndex, company})"
                  />
                </div>
                <div v-if="header.attr === 'status'">
                  <i
                    v-if="item.status === DIR_STATUS.CART"
                    class="fa fa-shopping-cart cart-icon"
                    aria-hidden="true"
                  />
                  <i
                    v-if="item.status === DIR_STATUS.REJECTED"
                    class="fa fa-ban reject-icon"
                    aria-hidden="true"
                  />
                </div>
                <span
                  v-if="!isTenant && header.attr === 'engName' && !company.chsName && hasMaintainDirectoryPerms && item.type === 'LOBBY' && company.chtName"
                  class="no-cht-name"
                >
                  {{ $t('directory.hint.chinese') }}
                </span>
              </td>
              <td
                v-if="showAction"
                class="action"
              >
                <template v-if="cIndex === 0">
                  <div class="action__btns">
                    <DirectoryActions
                      v-if="showDirAction(item)"
                      :item="item"
                      :openLifecycleFun="openRejectHistoriesModel"
                      @refresh:all="$emit('refresh:all')"
                    />
                    <template v-if="showDirCheck(item)">
                      <i
                        v-if="canCurrUserApprove"
                        class="fa fa-check-circle-o pointer"
                        :class="[{active: pendingApprItems[item.id]}]"
                        @click="addToAppr(item)"
                      />
                      <i
                        v-if="canCurrUserApprove"
                        class="fa fa-times-circle-o pointer"
                        :class="{active: pendingRejectItems[item.id]}"
                        @click="addToReject(item)"
                      />
                    </template>
                  </div>
                </template>
              </td>
            </tr>
            <template v-for="(subComp, sIndex) in company.subCompanies">
              <tr
                v-if="!collapseItems[index]"
                :key="subComp.id"
                class="sub-company"
                :class="(index + 1) % 2 === 1 ? 'odd-item' : 'even-item'"
              >
                <td />
                <td
                  v-for="header in headers"
                  :key="header"
                  :class="header.cls"
                >
                  <span :class="{'field-hightLight': setHightLigth(item, `companies.${cIndex}.subCompanies.${sIndex}.${header.attr}`)}">
                    {{ subComp[header.attr] || getEmptyValue(header.emptyValue, 'sub') }}
                  </span>
                  <span
                    v-if="!isTenant && header.attr === 'engName' && !subComp.chsName && hasMaintainDirectoryPerms && item.type === 'LOBBY' && subComp.chtName"
                    class="no-cht-name"
                  >
                    {{ $t('directory.hint.chinese') }}
                  </span>
                </td>
                <td v-if="showAction" />
              </tr>
              <tr
                v-for="(detail, dIndex) in subComp.subDetails"
                v-if="!collapseItems[index]"
                :key="detail.id"
                class="sub-detail"
                :class="(index + 1) % 2 === 1 ? 'odd-item' : 'even-item'"
              >
                <td />
                <td
                  v-for="header in headers"
                  :key="header"
                  :class="header.cls"
                >
                  <span :class="{'field-hightLight': setHightLigth(item, `companies.${cIndex}.subCompanies.${sIndex}.subDetails.${dIndex}.${header.attr}`)}">
                    {{ detail[header.attr] || getEmptyValue(header.emptyValue, 'detail') }}
                  </span>
                </td>
                <td v-if="showAction" />
              </tr>
            </template>
          </template>
        </template>
      </tbody>
    </table>
    <ActionConfirmModal
      ref="rejectModal"
      modalId="reject-directory-modal"
      :confirmBtnName="$t('action.reject')"
      :confirmTitle="rejectCommentTitle"
      :disabledConfirmBtn="disabledConfirmBtn"
    >
      <div slot="content">
        <label class="font-bold">{{ $t('comment') }}</label>
        <TextField
          :value="rejectComment"
          :required="true"
          :placeholder="$t('hint.typeRejectComment')"
          @inputValue="onUpdateRejectComment"
        />
      </div>
    </ActionConfirmModal>
    <Lifecycle
      ref="directoryRejectLifecycle"
      :modalId="`directory-commments-modules-${tableType}-lifecycle-modal`"
      :type="'directoryCommentsHistory'"
      :commentAttr="lifecycleCommentAttrs.comments"
      :showStatusCol="false"
      :histories="rejectHistories"
      :emptyHistoryHint="emptyCommentsHint"
      :allowPreformattedComments="true"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'
import { watch, computed, toRefs, unref } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import TextField from '@/controls/TextField'
import Lifecycle from '@/modules/Lifecycle'
import { utils as formUtils } from '@/plugin/InputChange'
import { useStoreGetters } from '@/store/index'
import navigation from '@/utils/navigation'
import { lifecycleCommentAttrs } from '@/utils/consts'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { DIR_STATUS, DIR_TYPE } from './consts'
import { isDuplicatedDirectory } from './utils'
import DirectoryActions from './DirectoryActions'

const DUPLICATE_OFFSET = 0.1

export default {
  name: 'DirectoryTable',
  components: {
    ActionConfirmModal,
    DirectoryActions,
    TextField,
    Lifecycle,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    originItems: {
      type: Array,
      default: () => [],
    },
    showAction: {
      type: Boolean,
      default: true,
    },
    tableType: {
      type: String,
      default: DIR_TYPE.LOBBY,
    },
  },
  setup (props, { emit }) {
    let returnData = { }
    const { items, showAction, originItems } = toRefs(props)
    const { isEngineerRef } = useStoreGetters('user', ['isEngineer'])

    const itemMap = computed(() => {
      return lodash.keyBy(unref(originItems), 'id')
    })
    const tableContent = computed(() => {
      let counter = 0
      // The directories is copied and assigned with orders
      // The order of the duplicated directory will be its parent's order + DUPLICATE_OFFSET
      const itemsWithOrder = lodash.cloneDeep(unref(items))
        .map((item) => {
          if (!isDuplicatedDirectory(item, unref(itemMap)[item.sourceId])) {
            counter = counter + 1
            item.innerOrder = counter
            item.engNameOrder = item.companies[0].engName ? item.companies[0].engName.toUpperCase() : item.companies[0].engName
            if (item.child) {
              item.child.innerOrder = counter + DUPLICATE_OFFSET
              item.child.engNameOrder = item.engNameOrder
            }
          }
          return item
        })
      return lodash.orderBy(itemsWithOrder, ['engNameOrder', 'innerOrder'], ['asc', 'asc'])
    })
    const showDirCheck = (item) => {
      const isSubmitted = item.status === DIR_STATUS.SUBMITTED
      const isDuplicated = isDuplicatedDirectory(item, unref(itemMap)[item.sourceId])
      return unref(isEngineerRef) && isSubmitted && !isDuplicated
    }
    returnData = {
      ...returnData,
      itemMap,
      tableContent,
      showDirCheck,
    }

    const dirCheck = computed(() => {
      if (!unref(showAction)) return false
      const list = unref(tableContent)
      const result = list.some((item) => showDirCheck(item))
      return result
    })
    watch(dirCheck, () => {
      emit('has:dirCheck', dirCheck.value)
    }, { immediate: true })
    returnData = {
      ...returnData,
      dirCheck,
    }

    return returnData
  },
  data () {
    const emptyValue = this.$t('emptyValue')
    const basicHeaders = [{
      title: this.$t('directory.label.officeName'),
      cls: 'engName',
      attr: 'engName',
      routerLink: true,
      emptyValue,
    }, {
      title: this.$t('directory.label.officeNameCh'),
      cls: 'chtName',
      attr: 'chtName',
      emptyValue,
    }, {
      title: this.$t('directory.label.floor'),
      cls: 'floor',
      attr: 'floor',
      emptyValue: {
        main: emptyValue,
        sub: '',
      },
    }, {
      title: this.$t('directory.label.effectiveDate'),
      cls: 'effective-date',
      attr: 'effectiveDate',
      optional: true, // It is for level 2 and level 3 row
    }, {
      title: this.$t('directory.label.removeDate'),
      cls: 'remove-date',
      attr: 'removeDate',
      optional: true,
    }, {
      title: this.$t('directory.label.lastSubmittedDate'),
      cls: 'submittedAt',
      attr: 'submittedAt',
      optional: true,
    }, {
      title: this.$t('directory.label.status'),
      cls: 'status',
      attr: 'status',
      optional: true,
    }]
    if (this.tableType === DIR_TYPE.FLOOR) {
      basicHeaders.splice(3, 0, {
        title: this.$t('directory.label.premises'),
        cls: 'premises',
        attr: 'premises',
        emptyValue: {
          main: emptyValue,
          sub: '',
        },
      })
    }
    const engineerHeaders = [...basicHeaders, {
      title: this.$t('directory.label.publishedDate'),
      cls: 'submittedAt',
      attr: 'publishedAt',
      emptyValue: {
        main: emptyValue,
        sub: '',
      },
    }]
    return {
      basicHeaders,
      collapseItems: [],
      disabledConfirmBtn: true,
      DIR_STATUS,
      emptyValue,
      engineerHeaders,
      rejectCommentTitle: this.$t('directory.hint.reject'),
      rejectComment: '',
      pendingApprItems: {},
      pendingRejectItems: {},
      rejectHistories: [],
      lifecycleCommentAttrs,
      emptyCommentsHint: this.$t('directory.hint.emptyCommentsHint'),
    }
  },
  computed: {
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('directories', ['canCurrUserApprove']),
    headers () {
      return this.isEngineer ? this.engineerHeaders : this.basicHeaders
    },
    hasMaintainDirectoryPerms () {
      return aclHelper.getAccess(tmsPermsTypes.directory.maintain)
    },
    detailRouterName () {
      return this.isEngineer ? 'directoryDetailView' : 'tenantDirectoryDetailView'
    },
  },
  methods: {
    collapse (index) {
      this.collapseItems[index] = !this.collapseItems[index]
      this.$forceUpdate()
    },
    getRejectComment (item) {
      // TODO @Michelle, to be confirmed where to display reject/approve comment
      if (!item.status.includes(DIR_STATUS.REJECTED)) {
        return ''
      }
      const histories = item.actionHistory || []
      const history = lodash.orderBy(histories, 'updatedAt').reverse()
        .find(item => [this.$t('action.approve'), this.$t('action.reject')].includes(item.action))
      return (history || {}).comment
    },
    showTooltip (index, attr) {
      return index === 0 && attr === 'status'
    },
    navToDetail (item) {
      const name = this.isTenant ? 'tenantDirectoryDetail' : 'directoryDetail'
      navigation.toPage(name, { id: item.id })
    },
    getEmptyValue (emptyOption, type) {
      return typeof emptyOption === 'object' ? emptyOption[type]
        : emptyOption
    },
    removePendingAppr (item) {
      this.$delete(this.pendingApprItems, item.id)
      if (item.child && this.pendingApprItems[item.child.id]) {
        this.$delete(this.pendingRejectItems, item.child.id)
      }
    },
    removePendingReject (item) {
      this.$delete(this.pendingRejectItems, item.id)
      if (item.child && this.pendingRejectItems[item.child.id]) {
        this.$delete(this.pendingRejectItems, item.child.id)
      }
    },
    addToAppr (item) {
      if (this.pendingApprItems[item.id]) {
        this.removePendingAppr(item)
      } else {
        this.removePendingReject(item)
        const copiedItem = lodash.cloneDeep(item)
        delete copiedItem.innerOrder
        delete copiedItem.engNameOrder
        this.$set(this.pendingApprItems, item.id, copiedItem)
        if (copiedItem.child) {
          delete copiedItem.child.innerOrder
          delete copiedItem.child.engNameOrder
          this.$set(this.pendingApprItems, copiedItem.child.id, copiedItem.child)
        }
      }
      this.$emit('update:pendingItems', true, this.tableType, this.pendingApprItems)
      this.$emit('update:pendingItems', false, this.tableType, this.pendingRejectItems)
    },
    addToReject (item) {
      if (this.pendingApprItems[item.id]) {
        this.removePendingAppr(item)
      }
      if (this.pendingRejectItems[item.id]) {
        this.removePendingReject(item)
        this.$emit('update:pendingItems', true, this.tableType, this.pendingApprItems)
        this.$emit('update:pendingItems', false, this.tableType, this.pendingRejectItems)
        return
      }
      this.$refs.rejectModal.open(this.rejectDirectory, [item])
    },
    rejectDirectory (item) {
      this.$delete(this.pendingApprItems, item.id)
      const copiedItem = lodash.cloneDeep(item)
      delete copiedItem.innerOrder
      delete copiedItem.engNameOrder
      this.$set(this.pendingRejectItems, item.id, copiedItem)
      this.pendingRejectItems[item.id].comment = this.rejectComment
      if (copiedItem.child) {
        if (this.pendingApprItems[copiedItem.child.id]) {
          this.$delete(this.pendingApprItems, copiedItem.child.id)
        }
        delete copiedItem.child.innerOrder
        delete copiedItem.child.engNameOrder
        this.$set(this.pendingRejectItems, copiedItem.child.id, copiedItem.child)
        this.pendingRejectItems[copiedItem.child.id].comment = this.rejectComment
      }
      this.$emit('update:pendingItems', true, this.tableType, this.pendingApprItems)
      this.$emit('update:pendingItems', false, this.tableType, this.pendingRejectItems)
      this.onUpdateRejectComment('')
      formUtils.resetDirty()
    },
    onUpdateRejectComment (val) {
      this.disabledConfirmBtn = !val
      this.rejectComment = val
    },
    openRejectHistoriesModel (rejectHistories) {
      this.rejectHistories = rejectHistories
      this.$refs.directoryRejectLifecycle.init()
    },
    showDirAction (item) {
      const isDuplicated = isDuplicatedDirectory(item, this.itemMap[item.sourceId])
      return item.status === DIR_STATUS.PUBLISHED || !isDuplicated
    },
    setHightLigth (directory, path) {
      // temporarily disable highlighting for R7 HKLDCMS-25830
      if (!undefined) return false
      const parentDir = this.itemMap[directory.sourceId]
      let isHightLight = false
      const checkedFields = ['chtName', 'engName', 'floor']
      if (parentDir) {
        const isDuplicated = isDuplicatedDirectory(directory, parentDir)
        if (isDuplicated) {
          const pathArr = path.split('.')
          const field = pathArr[pathArr.length - 1]
          if (checkedFields.includes(field)) {
            const parentValue = lodash.get(parentDir, path)
            const value = lodash.get(directory, path)
            isHightLight = value !== parentValue
          }
        }
      }
      return isHightLight
    },
    vHtmlMainValue (payLoad) {
      const {header, isLevelOne, company} = payLoad
      const isShowed = header.optional
      return isShowed && isLevelOne ? '' : (company[header.attr] || this.getEmptyValue(header.emptyValue, 'main'))
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings';
@import '~styles/table';
@import '~styles/directories/common';

.directory-table {
  min-width: 1320px;
  th.engName {
      padding-left: 79px;
    }
  td {
    padding: 2px 20px 5px;
    height: 30px;
  }
  .main-comp {
    border-bottom: none;
  }
  .sub-company {
    td:nth-child(2), td:nth-child(3) {
      padding-left: 30px;
    }
  }

  .sub-detail {
    td:nth-child(2), td:nth-child(3) {
      padding-left: 40px;
    }
  }

  .arrow-down::before {
    content: "\F107";
    display: inline-block;
    font: normal normal normal 14px/1 FontAwesome;
    /*font-size: inherit;*/
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    font-size: 25px;
  }

  .arrow-up::before {
    content: "\F107";
    display: inline-block;
    font: normal normal normal 14px/1 FontAwesome;
    /*font-size: inherit;*/
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    font-size: 25px;
    transform: rotate(180deg);
  }
  .collapse-icon {
    width: 1%;
  }
  .engName, .chtName {
    width: 18%;
  }
  .floor {
    width: 10%;
  }
  .premises {
    width: 15%
  }
  .effective-date, .remove-date {
    width: 7%;
  }
  .submittedAt {
    width: 8%;
  }
  .status {
    width: 8%;
    .reject-icon {
      color: red;
      font-size: 16px;
    }
    .cart-icon {
      color: #e69500;
      font-size: 16px;
    }
  }
  .action {
    width: 3%;
    .action__btns {
      display: flex;
      align-items: center;
    }
    .directory-actions {
      display: inline-block;
      vertical-align: middle;
    }
    .fa {
      font-size: 24px;
      padding: 0 6px;
      &.active {
        color: $green
      }
      &.fa-times-circle-o.active {
        color: $red
      }
    }
  }
  .tooltip {
    position: relative;
    display: inline-block;
    color: #0C3A58;
    font-size: 14px;

    .tooltip-text {
      visibility: hidden;
      width: 200px;
      background-color: rgb(240, 240, 240);
      color: rgb(12, 58, 88);
      text-align: center;
      border-radius: 6px;
      padding: 5px 0;
      position: absolute;
      z-index: 1;
    }
    &:hover .tooltip-text {
      visibility: visible;
    }
  }
  .field-hightLight {
    color: $orange
  }
  &__value {
    display: inline;
  }
  a {
    text-decoration: underline;
  }
}
#reject-directory-modal {
  & ::v-deep .text-input textarea {
    width: 100%
  }
}
</style>
